import { Info, Loader2, RefreshCw, FolderInput, Play } from "lucide-react";

import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonTip } from "@/components/ui/button-tip";
import type { AdminLeadDetail, AdoptionState, ImportState } from "../../OnboardingAPI";
import { formatDateTime } from "./leadLabels";

export const ADOPTION_TEXT: Record<AdoptionState, string> = {
  waiting_for_customer: "Wartet auf den Mandanten.",
  pending: "Wird gleich übernommen.",
  running: "Wird übernommen ...",
  done: "Übernommen.",
  failed: "Übernehmen fehlgeschlagen.",
};

export const IMPORT_TEXT: Record<ImportState, string> = {
  not_started: "Wartet auf deine Prüfung. Sieh dir die Dateien an, dann starte den Import.",
  waiting_for_files: "Wartet auf die Dateien im Mandanten.",
  waiting_for_buildings: "Wartet auf eine Gebäudeliste.",
  running: "Läuft ...",
  done: "Fertig.",
  failed: "Fehlgeschlagen.",
  skipped: "Übersprungen.",
};

/** How many names of files that were not evaluated are listed; the rest is counted. */
export const NOT_PROCESSED_VISIBLE = 10;

const names = (list: unknown): string[] => (Array.isArray(list) ? list.filter((name): name is string => typeof name === "string" && name !== "") : []);

const VARIANT = { done: "success", failed: "destructive", skipped: "outline", running: "default" } as const;

/** The short mark for the list: what became of the lead's data, from the states only. */
export function dataMark(adoption?: AdoptionState, importState?: ImportState): "Übernommen" | "Ausstehend" | "Fehler" | null {
  // taken over and waiting for an administrator to start the import: the files are where they should be
  if (adoption === "done" && importState === "not_started") return "Übernommen";
  if (!adoption && !importState) return null;
  if (adoption === "failed" || importState === "failed") return "Fehler";
  if (adoption === "done" && (importState === "done" || importState === "skipped" || importState === undefined)) return "Übernommen";
  return "Ausstehend";
}

/** The reason the import cannot be started yet, from the same condition that disables the button. */
export const IMPORT_BLOCKED_REASON = "Die Dateien sind noch nicht im Mandanten.";

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <>
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="min-w-0 whitespace-pre-wrap break-words">{value === undefined || value === "" ? "-" : value}</dd>
  </>
);

/**
 * "Daten im Mandanten": the uploads that moved into the tenant's data space and the site, buildings and assets
 * made from them. Everything the server says is shown as text; the two buttons are offered only where the server
 * can accept them (it answers 409 otherwise), and while something runs there is only "Neu laden".
 */
export default function TenantDataBlock({
  detail,
  busy,
  hint,
  onAdopt,
  onImport,
  onReload,
}: {
  detail: AdminLeadDetail;
  busy: string | null;
  hint: string | null;
  onAdopt: () => void;
  onImport: () => void;
  onReload: () => void;
}) {
  const { adoption, import: imported, provisioning } = detail;
  const [confirmImport, setConfirmImport] = useState(false);
  if (!adoption && !imported) return null;
  const running = adoption?.state === "running" || imported?.state === "running";
  const canAdopt = !!adoption && provisioning?.state === "done" && (adoption.state === "failed" || adoption.state === "pending" || adoption.state === "waiting_for_customer");
  const filesInTenant = adoption?.state === "done";
  const notStarted = imported?.state === "not_started";
  const canImport = !!imported && filesInTenant && (notStarted || imported.state === "failed" || imported.state === "waiting_for_files" || imported.state === "waiting_for_buildings");
  const skipped = imported?.buildings?.skipped ?? 0;
  const notProcessed = names(adoption?.notProcessed);
  const importWarnings = names(imported?.warnings);
  const sizeOnly = names(adoption?.sizeOnlyChecked);
  return (
    <section className="space-y-3 border-t pt-5" data-testid="tenant-data">
      <h3 className="text-base font-semibold">Daten im Mandanten</h3>

      {adoption && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-medium">Dateien</h4>
            <Badge variant={VARIANT[adoption.state as keyof typeof VARIANT] ?? "secondary"} data-testid="adoption-state">{ADOPTION_TEXT[adoption.state]}</Badge>
          </div>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[10rem_1fr]">
            <Row label="Dateien übernommen" value={`${adoption.movedFiles} von ${adoption.totalFiles}`} />
            <Row label="Logo übernommen" value={adoption.logoAdopted === undefined ? undefined : adoption.logoAdopted ? "Ja" : "Nein"} />
            <Row label="Begonnen" value={adoption.startedAt ? formatDateTime(adoption.startedAt) : undefined} />
            <Row label="Abgeschlossen" value={adoption.completedAt ? formatDateTime(adoption.completedAt) : undefined} />
          </dl>
          {adoption.state === "failed" && adoption.error && (
            <p role="alert" className="text-sm text-destructive" data-testid="adoption-error">{adoption.error}</p>
          )}
        </div>
      )}

      {notProcessed.length > 0 && (
        <div role="note" className="flex items-start gap-3 rounded-2xl border border-border bg-white p-3 text-sm shadow-sm dark:bg-card" data-testid="not-processed">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 space-y-1">
            <p className="font-medium">Diese Dateien liegen im Datenraum, wurden aber noch nicht ausgewertet</p>
            <ul className="list-disc space-y-0.5 pl-5">
              {notProcessed.slice(0, NOT_PROCESSED_VISIBLE).map((name, index) => (
                <li key={`${name}-${index}`} className="break-all">{name}</li>
              ))}
            </ul>
            {notProcessed.length > NOT_PROCESSED_VISIBLE && (
              <p className="text-muted-foreground" data-testid="not-processed-more">… und {notProcessed.length - NOT_PROCESSED_VISIBLE} weitere</p>
            )}
          </div>
        </div>
      )}

      {importWarnings.length > 0 && (
        <div role="note" className="flex items-start gap-3 rounded-2xl border border-border bg-white p-3 text-sm shadow-sm dark:bg-card" data-testid="import-warnings">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 space-y-1">
            <p className="font-medium">Hinweise zum Import</p>
            <ul className="list-disc space-y-0.5 pl-5">
              {importWarnings.slice(0, NOT_PROCESSED_VISIBLE).map((text, index) => (
                <li key={`${text}-${index}`} className="break-words">{text}</li>
              ))}
            </ul>
            {importWarnings.length > NOT_PROCESSED_VISIBLE && (
              <p className="text-muted-foreground" data-testid="import-warnings-more">… und {importWarnings.length - NOT_PROCESSED_VISIBLE} weitere</p>
            )}
          </div>
        </div>
      )}

      {imported && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-medium">Gebäude und Anlagen</h4>
            {!notStarted && <Badge variant={VARIANT[imported.state as keyof typeof VARIANT] ?? "secondary"} data-testid="import-state">{IMPORT_TEXT[imported.state]}</Badge>}
          </div>
          {notStarted && (
            <p className="text-sm" data-testid="import-state">{IMPORT_TEXT.not_started}</p>
          )}
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[10rem_1fr]">
            <Row label="Liegenschaft" value={imported.siteName} />
            <Row label="Gebäude angelegt" value={imported.buildings ? String(imported.buildings.created) : undefined} />
            <Row label="Gebäude übersprungen" value={imported.buildings ? String(skipped) : undefined} />
            <Row label="Anlagen angelegt" value={imported.assets?.created === undefined ? undefined : String(imported.assets.created)} />
            <Row label="Anlagen aktualisiert" value={imported.assets?.updated === undefined ? undefined : String(imported.assets.updated)} />
            <Row label="Anlagen nicht zugeordnet" value={imported.assets?.unassigned === undefined ? undefined : String(imported.assets.unassigned)} />
            <Row label="Begonnen" value={imported.startedAt ? formatDateTime(imported.startedAt) : undefined} />
            <Row label="Abgeschlossen" value={imported.completedAt ? formatDateTime(imported.completedAt) : undefined} />
          </dl>
          {skipped > 0 && (
            <p className="text-sm text-muted-foreground" data-testid="buildings-skipped-note">
              Anschrift unvollständig, nicht angelegt: {skipped}.
            </p>
          )}
          {imported.assets?.summary && (
            <p className="whitespace-pre-wrap break-words text-sm" data-testid="import-summary">{imported.assets.summary}</p>
          )}
          {imported.state === "failed" && imported.error && (
            <p role="alert" className="text-sm text-destructive" data-testid="import-error">{imported.error}</p>
          )}
        </div>
      )}

      {sizeOnly.length > 0 && (
        <details className="text-xs text-muted-foreground" data-testid="size-only">
          <summary className="cursor-pointer select-none">
            Bei {sizeOnly.length} {sizeOnly.length === 1 ? "Datei" : "Dateien"} wurde die Kopie nur nach der Größe geprüft
          </summary>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {sizeOnly.slice(0, NOT_PROCESSED_VISIBLE).map((name, index) => (
              <li key={`${name}-${index}`} className="break-all">{name}</li>
            ))}
          </ul>
          {sizeOnly.length > NOT_PROCESSED_VISIBLE && <p className="mt-1" data-testid="size-only-more">… und {sizeOnly.length - NOT_PROCESSED_VISIBLE} weitere</p>}
        </details>
      )}

      <div className="flex flex-wrap gap-2">
        {running ? (
          <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Lädt den Stand vom Server neu"} disabled={busy !== null}>
<Button variant="outline" onClick={onReload} disabled={busy !== null} data-testid="data-reload">
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Neu laden
          </Button>
</ButtonTip>
        ) : (
          <>
            {canAdopt && (
              <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Übernimmt die Dateien noch einmal in den Datenraum des Mandanten"} disabled={busy !== null}>
<Button variant="outline" onClick={onAdopt} disabled={busy !== null} data-testid="adopt-files">
                {busy === "adopt" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <FolderInput className="mr-2 h-4 w-4" aria-hidden="true" />}
                Dateien erneut übernehmen
              </Button>
</ButtonTip>
            )}
            {notStarted ? (
              <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : !canImport ? IMPORT_BLOCKED_REASON : "Legt Liegenschaft und Gebäude aus der Gebäudeliste an und importiert die Anlagen. Bereitet dafür die Listen auf."} disabled={busy !== null || !canImport}>
<Button onClick={() => setConfirmImport(true)} disabled={busy !== null || !canImport} data-testid="start-import-first">
                {busy === "import" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Play className="mr-2 h-4 w-4" aria-hidden="true" />}
                Gebäude und Anlagen importieren
              </Button>
</ButtonTip>
            ) : (
              canImport && (
                <ButtonTip label={busy !== null ? "Eine andere Aktion läuft noch." : "Startet den Import von Gebäuden und Anlagen noch einmal"} disabled={busy !== null}>
<Button variant="outline" onClick={onImport} disabled={busy !== null} data-testid="start-import">
                  {busy === "import" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Play className="mr-2 h-4 w-4" aria-hidden="true" />}
                  Import erneut starten
                </Button>
</ButtonTip>
              )
            )}
          </>
        )}
      </div>
      {notStarted && !canImport && !running && (
        <p className="text-sm text-muted-foreground" data-testid="import-blocked-reason">{IMPORT_BLOCKED_REASON}</p>
      )}
      <AlertDialog open={confirmImport} onOpenChange={setConfirmImport}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gebäude und Anlagen importieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Erst jetzt werden die Listen aufbereitet und verarbeitet; das kostet Rechenzeit. Gebäude werden nur bei vollständiger Anschrift
              angelegt, übersprungene Zeilen stehen danach in diesem Block. Der Import lässt sich wiederholen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmImport(false);
                onImport();
              }}
            >
              Import starten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {hint && (
        <p role="status" className="text-sm text-muted-foreground" data-testid="data-hint">
          {hint}
        </p>
      )}
    </section>
  );
}
